import axios from 'axios';
import { useEffect, useState } from 'react';

const API_BASE = import.meta.env.VITE_API_URL || 'https://roniapi.data-corner.com.au';
const TOKEN_KEY = 'client-auth-token';
const CLIENT_KEY = 'client-auth-client';
const REMEMBERED_PHONE_KEY = 'client-remembered-phone';
const HAS_PIN_KEY = 'client-has-pin';
const PIN_UNLOCKED_KEY = 'client-pin-unlocked'; // sessionStorage - cleared when tab/app closes

export function getHasPinCached(): boolean {
  return localStorage.getItem(HAS_PIN_KEY) === '1';
}
export function setHasPinCached(v: boolean): void {
  if (v) localStorage.setItem(HAS_PIN_KEY, '1');
  else localStorage.removeItem(HAS_PIN_KEY);
}
export function isPinUnlocked(): boolean {
  try { return sessionStorage.getItem(PIN_UNLOCKED_KEY) === '1'; } catch { return false; }
}
export function markPinUnlocked(): void {
  try { sessionStorage.setItem(PIN_UNLOCKED_KEY, '1'); } catch {}
}
export function clearPinUnlocked(): void {
  try { sessionStorage.removeItem(PIN_UNLOCKED_KEY); } catch {}
}

export interface ClientAppClient {
  id: string;
  tenantId: string;
  tenantName?: string;
  displayName: string;
  email?: string | null;
  phone?: string | null;
}

export interface ClientBusiness {
  id: string;
  displayName: string;
  email?: string | null;
  phone?: string | null;
  abn?: string | null;
  acn?: string | null;
  tfn?: string | null;
  industry?: string | null;
  status: string;
  totalDocuments: number;
  indexedDocuments: number;
  totalSizeBytes: number;
  lastDocumentAt?: string | null;
  aiSummary?: string | null;
  tags: string[];
  keyDates: Array<{ label?: string; date?: string; name?: string }>;
  xeroOrgName?: string | null;
  xeroLastSyncedAt?: string | null;
  categoryBreakdown: Array<{ category: string; count: number }>;
  yearBreakdown: Array<{ year: string; count: number }>;
  tenant: { id: string; name: string };
  xeroOrgId?: string | null;
}

export function authHeader(): Record<string, string> {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const api = axios.create({ baseURL: API_BASE });

export async function requestCode(phone: string): Promise<void> {
  await api.post('/client/auth/request-code', { phone });
}

export async function verifyCode(phone: string, code: string): Promise<{ token: string; client: ClientAppClient }> {
  const r = await api.post<{ token: string; client: ClientAppClient }>('/client/auth/verify-code', { phone, code });
  localStorage.setItem(TOKEN_KEY, r.data.token);
  localStorage.setItem(CLIENT_KEY, JSON.stringify(r.data.client));
  localStorage.setItem(REMEMBERED_PHONE_KEY, phone);
  // SMS verification is a full unlock - don't re-prompt for PIN this session.
  markPinUnlocked();
  return r.data;
}

export async function getMe(): Promise<ClientAppClient> {
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) throw new Error('Not authenticated');
  const r = await api.get<{ client: ClientAppClient }>('/client/auth/me', {
    headers: { Authorization: `Bearer ${token}` },
  });
  localStorage.setItem(CLIENT_KEY, JSON.stringify(r.data.client));
  return r.data.client;
}

export function getStoredClient(): ClientAppClient | null {
  try {
    const raw = localStorage.getItem(CLIENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function isAuthenticated(): boolean {
  return !!localStorage.getItem(TOKEN_KEY);
}

export function logout(opts?: { keepRememberedPhone?: boolean }): void {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(CLIENT_KEY);
  if (!opts?.keepRememberedPhone) localStorage.removeItem(REMEMBERED_PHONE_KEY);
  clearPinUnlocked();
}

export async function getBusiness(): Promise<ClientBusiness> {
  const r = await api.get<{ business: ClientBusiness }>('/client/me/business', { headers: authHeader() });
  return r.data.business;
}

export type FinancialPeriod = 'quarter' | 'year';

export interface AgedBuckets {
  current: number;
  d30: number;
  d60: number;
  d90: number;
  d90plus: number;
}

export interface ClientFinancials {
  connected: boolean;
  xeroOrgName: string | null;
  message?: string;
  asOf?: string;
  industry?: string | null;
  period?: FinancialPeriod;
  periodLabel?: string;
  isDraft?: boolean;
  usingPrevFyAsCurrent?: boolean;
  fallbackNote?: string;
  current?: { revenue: number; expenses: number; profit: number; marginPct: number };
  previous?: { revenue: number; expenses: number; profit: number; marginPct: number };
  deltas?: {
    revenuePct: number | null;
    expensesPct: number | null;
    profitPct: number | null;
    marginPctPoints: number | null;
  };

  bas?: {
    quarterLabel: string;
    quarterStart: string;
    quarterEnd: string;
    daysUntilDue: number;
    dueDate: string;
    gstCollected: number;
    gstPaid: number;
    netGstOwed: number;
    moneyIn: number;
    moneyOut: number;
  };

  receivables?: {
    total: number;
    buckets: AgedBuckets;
    overdueCount: number;
    topOutstanding: Array<{
      contact: string;
      amount: number;
      daysOverdue: number;
      dueDate: string;
      invoiceNumber: string;
    }>;
  };
  payables?: {
    total: number;
    ato: number;
    other: number;
    buckets: AgedBuckets;
    overdueCount: number;
    topOutstanding: Array<{
      contact: string;
      amount: number;
      daysOverdue: number;
      dueDate: string;
      isAto: boolean;
    }>;
  };

  quarterly?: Array<{
    label: string;
    start: string;
    end: string;
    revenue: number;
    profit: number;
    gstCollected: number;
    gstPaid: number;
    netGstOwed: number;
  }>;

  topCustomers?: Array<{ contact: string; amount: number }>;
  topSuppliers?: Array<{ contact: string; amount: number }>;
  topExpenses?: Array<{ category: string; amount: number }>;

  comparison?: {
    industry: string;
    source: { url: string; title: string };
    rows: Array<{
      label: string;
      yourPct: number;
      range: { min: number; max: number };
      higherIsBetter: boolean;
      verdict: 'in-range' | 'above-range' | 'below-range' | 'no-data';
    }>;
  };
  tips?: Array<{ title: string; body: string }>;
}

export async function getFinancials(period: FinancialPeriod = 'quarter'): Promise<ClientFinancials> {
  const r = await api.get<{ financials: ClientFinancials }>(`/client/me/financials?period=${period}`, {
    headers: authHeader(),
    timeout: 30_000,
  });
  return r.data.financials;
}

// ─── Deadlines (ATO compliance calendar for this client) ─────────────

export type DeadlineStatus = 'overdue' | 'due_soon' | 'upcoming';

export interface ClientDeadline {
  id: string;
  title: string;
  description: string;
  deadlineType: string;
  status: DeadlineStatus;
  dueDate: string;
  daysUntil: number;
  recurrence: string;
}

export interface ClientDeadlinesResponse {
  deadlines: ClientDeadline[];
  entityType: string;
}

export async function getDeadlines(): Promise<ClientDeadlinesResponse> {
  const r = await api.get<ClientDeadlinesResponse>('/client/me/deadlines', {
    headers: authHeader(),
    timeout: 15_000,
  });
  return r.data;
}

export interface ChatSource {
  documentId?: string;
  documentName?: string;
  excerpt?: string;
  relevanceScore?: number;
  type?: string;
  title?: string;
  url?: string;
}

export interface ChatReply {
  message: { id: string; role: 'assistant'; content: string; timestamp: string; citations?: ChatSource[] };
  sources: ChatSource[];
  model?: string;
}

export interface ChatHistoryItem {
  role: 'user' | 'assistant';
  content: string;
}

// ---------- Persistent conversations ----------

export interface ConversationSummary {
  id: string;
  title: string;
  scopeFileId?: string | null;
  lastSnippet?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: ChatSource[];
  createdAt: string;
}

export interface ConversationDetail {
  id: string;
  title: string;
  scopeFileId?: string | null;
  createdAt: string;
  updatedAt: string;
  messages: ConversationMessage[];
}

export async function listConversations(limit = 50): Promise<ConversationSummary[]> {
  const r = await api.get<{ conversations: ConversationSummary[] }>(`/client/me/conversations?limit=${limit}`, { headers: authHeader() });
  return r.data.conversations;
}

export async function getConversation(id: string): Promise<ConversationDetail> {
  const r = await api.get<{ conversation: ConversationDetail }>(`/client/me/conversations/${id}`, { headers: authHeader() });
  return r.data.conversation;
}

export async function deleteConversation(id: string): Promise<void> {
  await api.delete(`/client/me/conversations/${id}`, { headers: authHeader() });
}

export async function askInConversation(message: string, opts?: { conversationId?: string; fileId?: string; lang?: string }): Promise<{ conversationId: string; message: ChatReply['message']; sources: ChatSource[] }> {
  const r = await api.post<{ conversationId: string; message: ChatReply['message']; sources: ChatSource[] }>(
    '/client/me/conversations/ask',
    { message, conversationId: opts?.conversationId, fileId: opts?.fileId, lang: opts?.lang },
    { headers: authHeader(), timeout: 60_000 },
  );
  return r.data;
}

export async function ask(message: string, conversationHistory: ChatHistoryItem[] = [], opts?: { fileId?: string; dataSource?: 'both' | 'xero' | 'documents'; lang?: string }): Promise<ChatReply> {
  const r = await api.post<{ success: boolean; data: ChatReply }>(
    '/client/ask',
    { message, conversationHistory, dataSource: opts?.dataSource || 'both', fileId: opts?.fileId, lang: opts?.lang },
    { headers: authHeader(), timeout: 60_000 },
  );
  return r.data.data;
}

// ---------- Settings & PIN ----------

export interface ServerSettings {
  lang: string | null;
  themeMode: 'light' | 'dark';
  fontSize: 'normal' | 'large' | 'xl';
  accent: string;
  hasPin: boolean;
  pinSetAt: string | null;
}

export function getRememberedPhone(): string | null {
  return localStorage.getItem(REMEMBERED_PHONE_KEY);
}
export function setRememberedPhone(phone: string): void {
  localStorage.setItem(REMEMBERED_PHONE_KEY, phone);
}
export function clearRememberedPhone(): void {
  localStorage.removeItem(REMEMBERED_PHONE_KEY);
}

export async function fetchSettings(): Promise<ServerSettings> {
  const r = await api.get<{ settings: ServerSettings }>('/client/me/settings', { headers: authHeader() });
  return r.data.settings;
}

export async function saveSettings(patch: Partial<{ lang: string; themeMode: string; fontSize: string; accent: string }>): Promise<void> {
  await api.put('/client/me/settings', patch, { headers: authHeader() });
}

export async function setPin(pin: string): Promise<void> {
  await api.put('/client/me/pin', { pin }, { headers: authHeader() });
  setHasPinCached(true);
  markPinUnlocked();
}

export async function removePin(): Promise<void> {
  await api.delete('/client/me/pin', { headers: authHeader() });
  setHasPinCached(false);
  clearPinUnlocked();
}

export async function loginWithPin(phone: string, pin: string): Promise<{ token: string; client: ClientAppClient }> {
  const r = await api.post<{ token: string; client: ClientAppClient }>('/client/auth/pin', { phone, pin });
  localStorage.setItem(TOKEN_KEY, r.data.token);
  localStorage.setItem(CLIENT_KEY, JSON.stringify(r.data.client));
  setHasPinCached(true);
  markPinUnlocked();
  return r.data;
}

// ---------- Messages ----------

export interface ClientMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  body: string;
  attachmentFileId?: string | null;
  attachmentName?: string | null;
  attachmentContentType?: string | null;
  attachmentUrl?: string | null;
  senderName?: string | null;
  readAt?: string | null;
  createdAt: string;
}

export async function listMessages(limit = 50): Promise<{ messages: ClientMessage[]; unreadCount: number }> {
  const r = await api.get<{ messages: ClientMessage[]; unreadCount: number }>(`/client/me/messages?limit=${limit}`, { headers: authHeader() });
  return r.data;
}

export async function sendMessage(body: string, attachmentFileId?: string): Promise<{ message: ClientMessage }> {
  const r = await api.post<{ message: ClientMessage }>('/client/me/messages', { body, attachmentFileId }, { headers: authHeader() });
  return r.data;
}

export async function markMessagesRead(): Promise<void> {
  await api.put('/client/me/messages/read', {}, { headers: authHeader() });
}

// ---------- Timeline ----------

export interface TimelineEvent {
  id: string;
  eventType: string;
  title: string;
  description?: string | null;
  icon?: string | null;
  metadata?: any;
  occurredAt: string;
}

// ---------- Push notifications ----------

export async function getPushPublicKey(): Promise<string> {
  const r = await api.get<{ publicKey: string }>('/client/push/public-key');
  return r.data.publicKey;
}

export async function subscribePush(sub: PushSubscription): Promise<void> {
  await api.post('/client/me/push/subscribe', sub.toJSON(), { headers: authHeader() });
}

export async function unsubscribePush(endpoint?: string): Promise<void> {
  await api.post('/client/me/push/unsubscribe', { endpoint }, { headers: authHeader() });
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export async function enablePushNotifications(): Promise<{ ok: true } | { ok: false; reason: string }> {
  if (!('serviceWorker' in navigator)) return { ok: false, reason: 'Service workers not supported' };
  if (!('PushManager' in window)) return { ok: false, reason: 'Push not supported in this browser' };
  const perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: 'Permission denied' };
  const reg = await navigator.serviceWorker.ready;
  const publicKey = await getPushPublicKey();
  if (!publicKey) return { ok: false, reason: 'Server push not configured' };
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
  await subscribePush(sub);
  return { ok: true };
}

export async function disablePushNotifications(): Promise<void> {
  if (!('serviceWorker' in navigator)) return;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await unsubscribePush(sub.endpoint).catch(() => {});
      await sub.unsubscribe();
    }
  } catch (e) { console.warn('disablePush:', e); }
}

export async function getPushStatus(): Promise<'unsupported' | 'denied' | 'enabled' | 'disabled'> {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return sub ? 'enabled' : 'disabled';
  } catch {
    return 'disabled';
  }
}

export async function listTimeline(limit = 50): Promise<TimelineEvent[]> {
  const r = await api.get<{ events: TimelineEvent[] }>(`/client/me/timeline?limit=${limit}`, { headers: authHeader() });
  return r.data.events;
}

export interface FileTimelineItem {
  id: string;
  direction: 'sent' | 'received';
  title: string;        // short type label, e.g. "Receipt", "Bank statement"
  filename: string;
  contentType?: string | null;
  senderName?: string | null;
  occurredAt: string;
}

export async function listFilesTimeline(limit = 100): Promise<FileTimelineItem[]> {
  const r = await api.get<{ items: FileTimelineItem[] }>(`/client/me/files-timeline?limit=${limit}`, { headers: authHeader() });
  return r.data.items;
}

// ---------- Tips ----------

export interface Tip {
  id: string;
  tag?: string | null;
  titleEn: string;
  bodyEn: string;
  titleFa?: string | null;
  bodyFa?: string | null;
}

export async function listTips(): Promise<Tip[]> {
  const r = await api.get<{ tips: Tip[] }>('/client/me/tips', { headers: authHeader() });
  return r.data.tips;
}

// ---------- Files ----------

export interface ClientFile {
  id: string;
  filename: string;
  contentType: string;
  fileSizeBytes: number;
  category: string;
  financialYear?: string | null;
  processingStatus: string;
  source: string;
  createdAt: string;
}

export async function listFiles(category?: string): Promise<ClientFile[]> {
  const qs = category ? `?category=${encodeURIComponent(category)}` : '';
  const r = await api.get<{ files: ClientFile[] }>(`/client/me/files${qs}`, { headers: authHeader() });
  return r.data.files;
}

export async function getFileDownloadUrl(fileId: string): Promise<{ url: string; filename: string; contentType: string }> {
  const r = await api.get<{ url: string; filename: string; contentType: string }>(`/client/me/files/${fileId}/download`, { headers: authHeader() });
  return r.data;
}

export async function processFile(fileId: string): Promise<{ fileId: string; processingStatus: string }> {
  const r = await api.post<{ fileId: string; processingStatus: string }>(`/client/me/files/${fileId}/process`, {}, { headers: authHeader() });
  return r.data;
}

interface UploadUrlResp { uploadUrl: string; fileId: string; s3Key: string; }

export async function uploadFile(file: File, opts?: { note?: string; onProgress?: (pct: number) => void }): Promise<ClientFile> {
  const presign = await api.post<UploadUrlResp>('/client/me/files/upload-url', {
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    fileSizeBytes: file.size,
  }, { headers: authHeader() });

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', presign.data.uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    if (opts?.onProgress) {
      xhr.upload.addEventListener('progress', e => {
        if (e.lengthComputable) opts.onProgress!(Math.round((e.loaded / e.total) * 100));
      });
    }
    xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });

  const confirm = await api.post<{ file: ClientFile }>('/client/me/files', {
    fileId: presign.data.fileId,
    s3Key: presign.data.s3Key,
    filename: file.name,
    contentType: file.type || 'application/octet-stream',
    fileSizeBytes: file.size,
    note: opts?.note,
  }, { headers: authHeader() });

  return confirm.data.file;
}

/** Reactive hook - reads stored client from localStorage, re-renders on changes */
export function useClient(): ClientAppClient | null {
  const [client, setClient] = useState<ClientAppClient | null>(() => getStoredClient());
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === CLIENT_KEY || e.key === null) setClient(getStoredClient());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  return client;
}
